import { useEffect, useId, useRef, useState, type FormEvent, type ReactElement } from 'react';
import type { Role, User } from '@chatapp-inbox/shared';
import { isApiError, type UserPatch } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { adminErrorMessage, userErrorField } from './error-message';
import { Field } from './Field';
import { RoleField } from './RoleField';
import { validateName } from './validation';
import { useUpdateUser } from './use-users';
import styles from './forms.module.css';
import panelStyles from './EditUserPanel.module.css';

interface UserDataFormProps {
  user: User;
  viewerId: string;
}

/**
 * Nome e papel. Só os campos que mudaram vão ao servidor. O próprio papel fica travado: o servidor
 * deixaria um administrador se rebaixar (se houver outro ativo), e a pessoa ficaria sem acesso a
 * esta tela no meio do uso; quem pode rebaixá-lo é outro administrador.
 */
export function UserDataForm({ user, viewerId }: UserDataFormProps): ReactElement {
  const headingId = useId();
  const isYou = user.id === viewerId;
  const update = useUpdateUser(viewerId);
  // O resultado aparece junto do botão (um aviso no topo da lista ficaria fora da vista).
  const status = useStatusMessage();
  const [name, setName] = useState(user.name);
  const [role, setRole] = useState<Role>(user.role);
  const [nameError, setNameError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);

  // O painel acabou de abrir: o foco vai para o primeiro campo.
  useEffect(() => {
    nameRef.current?.focus();
  }, []);

  const pending = update.isPending;

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (pending) return;
    setFormError(null);
    const invalid = validateName(name);
    setNameError(invalid);
    if (invalid) {
      nameRef.current?.focus();
      return;
    }
    const patch: UserPatch = {};
    if (name.trim() !== user.name) patch.name = name.trim();
    if (!isYou && role !== user.role) patch.role = role;
    if (Object.keys(patch).length === 0) {
      status.announce('Nenhuma alteração para salvar.');
      return;
    }
    update.mutate(
      { id: user.id, patch },
      {
        onSuccess: (saved) => {
          setName(saved.name);
          setRole(saved.role);
          status.announce(`Alterações salvas: ${saved.name}.`);
        },
        onError: (error) => {
          const message = adminErrorMessage(error, 'Não foi possível salvar. Tente de novo.');
          if (userErrorField(error) === 'name') {
            setNameError(message);
            nameRef.current?.focus();
            return;
          }
          // O servidor disse que essa mudança é impossível: o papel volta ao que está valendo.
          if (isApiError(error) && error.code === 'last_admin') setRole(user.role);
          setFormError(message);
        },
      },
    );
  };

  return (
    <section aria-labelledby={headingId} className={panelStyles.block}>
      <h4 id={headingId} className={panelStyles.blockTitle}>
        Dados
      </h4>
      <form className={styles.form} noValidate aria-busy={pending || undefined} onSubmit={submit}>
        <Field
          ref={nameRef}
          label="Nome"
          dir="auto"
          autoComplete="off"
          value={name}
          readOnly={pending}
          error={nameError}
          onChange={(event) => {
            setName(event.target.value);
            setNameError(null);
          }}
        />
        <RoleField
          value={role}
          onChange={setRole}
          disabled={isYou || pending}
          note={
            isYou
              ? 'Você não pode alterar o seu próprio papel. Peça a outro administrador.'
              : undefined
          }
        />
        {formError && (
          <p role="alert" className={styles.alert}>
            {formError}
          </p>
        )}
        <div className={styles.actions}>
          <Button type="submit" variant="primary" busy={pending}>
            {pending ? 'Salvando…' : 'Salvar alterações'}
          </Button>
        </div>
        <StatusRegion message={status.message} />
      </form>
    </section>
  );
}
